<template>
  <v-stepper v-model="e1">
    <v-stepper-header>
      <v-stepper-step
        :complete="e1 > 1"
        step="1"
      >
        Settlement Options
      </v-stepper-step>

      <v-divider />

      <v-stepper-step
        :complete="e1 > 2"
        step="2"
      >
        Select Term and Amount
      </v-stepper-step>

      <v-divider />

      <v-stepper-step
        :complete="e1 > 3"
        step="3"
      >
        Payment Method
      </v-stepper-step>
    </v-stepper-header>

    <v-stepper-items>
      <!--Settlement Options-->
      <v-stepper-content
        class="settlement-amount-step"
        step="1"
      >
        <v-container>
          <v-row>
            <v-col cols="auto">
              <v-alert
                color="info"
                theme="dark"
                icon="mdi-bullhorn-outline"
              >
                <div class="text-h6">
                  Agent, Please read the following to customer:
                </div>
                Our reduced payoff plan allows you (the borrower) to choose how much
                you can afford to pay and over what term. You are eligible to select from the Max,
                Mid and Min options that we offer.
              </v-alert>
            </v-col>
          </v-row>
          <v-row
            justify="center"
          >
            <v-container fluid>
              <v-radio-group
                v-model="selectedPayoffOption"
              >
                <v-radio
                  v-for="(planOption, index) in payoffPlanOptions"
                  :key="`planOption-${index}`"
                  :value="planOption"
                  :disabled="!planOption.isEligible"
                  @click="getPaymentAmountForTermsInPlan"
                >
                  <template #label>
                    <v-row>
                      <v-col cols="6">
                        <p>Option {{ planOption.option }}: {{ planOption.title }}</p>
                        <p>({{ planOption.terms }} Months)</p>
                      </v-col>
                      <v-col
                        cols="3"
                        align-self="center"
                      >
                        <p>{{ planOption.discountedSettlementAmount | currency }}</p>
                      </v-col>
                      <v-col
                        cols="3"
                        align-self="center"
                      >
                        <v-chip :disabled="!planOption.isEligible">
                          {{ planOption.isEligible ? 'Eligible' : 'Not Eligible' }}
                        </v-chip>
                      </v-col>
                    </v-row>
                  </template>
                </v-radio>
              </v-radio-group>
            </v-container>
          </v-row>
          <v-row justify="space-between">
            <v-col
              cols="auto"
              align="left"
            >
              <v-btn
                outlined
                large
                color="primary"
                @click="goToSinglePaymentView"
              >
                <v-icon
                  left
                  dark
                >
                  mdi-arrow-left
                </v-icon>
                Single Payment Option
              </v-btn>
            </v-col>
            <v-col
              cols="auto"
              align="right"
            >
              <v-btn
                color="success"
                large
                :disabled="!selectedPayoffOption"
                @click="e1 = 2"
              >
                Continue to Select Terms
                <v-icon
                  right
                  dark
                >
                  mdi-arrow-right
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-stepper-content>
      <!--Settlement Options End-->

      <!--Terms and amount screen-->
      <v-stepper-content step="2">
        <v-container>
          <v-row justify="center">
            <v-col cols="auto">
              <h2> PAYOFF: {{ selectedPayoffOption?.discountedSettlementAmount | currency }}</h2>
            </v-col>
          </v-row>
          <v-row
            justify="center"
          >
            <v-container fluid>
              <v-radio-group
                v-model="selectedPayoffTerm"
              >
                <v-radio
                  v-for="(planTerm, index) in planTerms"
                  :key="`planTerm-${index}`"
                  :value="planTerm"
                >
                  <template #label>
                    <v-row>
                      <v-col
                        cols="auto"
                        align-self="center"
                      >
                        <p>
                          Pay over {{ planTerm.term }} Months,
                          {{ planTerm.paymentAmount | currency }} {{ paymentFrequencyStr }}
                        </p>
                      </v-col>
                    </v-row>
                  </template>
                </v-radio>
              </v-radio-group>
            </v-container>
          </v-row>
          <v-row justify="space-between">
            <v-col
              cols="auto"
              align="left"
            >
              <v-btn
                outlined
                large
                color="primary"
                @click="e1=1"
              >
                <v-icon
                  left
                  dark
                >
                  mdi-arrow-left
                </v-icon>
                Back
              </v-btn>
            </v-col>
            <v-col
              cols="auto"
              align="right"
            >
              <v-btn
                color="success"
                large
                :disabled="!selectedPayoffTerm"
                @click="e1 = 3"
              >
                Continue to Payment Method
                <v-icon
                  right
                  dark
                >
                  mdi-arrow-right
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-stepper-content>
      <!--Terms and amount screen End-->

      <!--Payment Method screen-->
      <v-stepper-content step="3">
        <v-form
          ref="singlePaymentForm"
          novalidate
          @submit.prevent="onSubmit"
        >
          <v-container>
            <v-row>
              <v-col
                cols="12"
                align="right"
              >
                <ticket-add-debit-card-dialog
                  :ticket="ticket"
                  @success="reloadPaymentProfiles"
                />
                <ticket-add-checking-dialog
                  :ticket="ticket"
                  @success="reloadPaymentProfiles"
                />
              </v-col>
              <v-col cols="12">
                <v-select
                  v-model="formData.paymentAccountId"
                  :items="paymentAccountsWithManualOption"
                  item-value="id"
                  item-text="title"
                  label="Payment Account"
                  outlined
                  required
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="formData.firstPaymentDate"
                  type="date"
                  label="First Payment Date In Plan"
                  disabled
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col
                v-if="displayCSRMessageForManualPayment"
                cols="12"
              >
                <v-alert
                  color="info"
                  theme="dark"
                  icon="mdi-bullhorn-outline"
                >
                  <div class="text-h6">
                    Agent, Please read to customer:
                  </div>
                  Please note that because you selected the manual payments option,
                  you will be responsible for completing your payment.
                </v-alert>
              </v-col>
              <v-col
                v-else-if="displayCSRMessageForAutopay"
                cols="12"
              >
                <v-alert
                  color="info"
                  theme="dark"
                  icon="mdi-bullhorn-outline"
                >
                  <div class="text-h6">
                    Agent, Please read to customer:
                  </div>
                  Please acknowledge that you have verbally consented to the ACH Authorization.
                  The Electronic disclosure will be included in your confirmation email.
                </v-alert>
                <v-checkbox
                  v-model="formData.achConsent"
                  label="Verbally Opt into Autopay"
                />
              </v-col>
            </v-row>
          </v-container>
          <v-row justify="space-between">
            <v-col
              cols="auto"
              align="left"
            >
              <v-btn
                outlined
                large
                color="primary"
                @click="e1 = 2"
              >
                <v-icon
                  left
                  dark
                >
                  mdi-arrow-left
                </v-icon>
                Back
              </v-btn>
            </v-col>
            <v-col
              cols="auto"
              align="right"
            >
              <v-btn
                color="success"
                large
                :disabled="createSettlePlanButtonDisabled"
                :loading="loading"
                @click="createSettlementReducedPayOffPlan"
              >
                Create Reduced Payoff Plan
                <v-icon
                  right
                  dark
                >
                  mdi-arrow-right
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-stepper-content>
      <!--Payment Method screen End-->
    </v-stepper-items>
  </v-stepper>
</template>

<script>
import TicketAddDebitCardDialog from '@/views/Tickets/AddPaymentDialogs/TicketAddDebitCardDialog.vue';
import TicketAddCheckingDialog from '@/views/Tickets/AddPaymentDialogs/TicketAddCheckingDialog.vue';
import { mapActions } from 'vuex';

export default {
  name: 'ReducedPayoffPlan',

  components: {
    TicketAddCheckingDialog,
    TicketAddDebitCardDialog,
  },

  props: {
    ticket: {
      type: Object,
      required: true,
    },
    paymentAccounts: {
      type: Array,
      required: true,
    },
    payoffPlanOptions: {
      type: Array,
      required: true,
    },
    firstPaymentDateInPlan: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      e1: 1,
      selectedPayoffOption: null,
      selectedPayoffTerm: null,
      planTerms: [],
      formData: {
        settlementAmount: null,
        paymentAmount: null,
        settlementTerm: null,
        firstPaymentDate: this.firstPaymentDateInPlan,
        paymentAccountId: null,
        achConsent: false,
        platform: 'HUI',
        optionId: null,
        totalCreditAmount: null,
        discountPercent: 0,
        isDebtConsolidation: false,
      },
      loading: false,
    };
  },

  computed: {
    paymentAccountsWithManualOption() {
      const manualItem = {
        active: 1,
        id: -1,
        is_primary: 0,
        is_secondary: 0,
        title: 'Manual Payments',
        type: 'paymentAccount.type.manual',
        visible: 1,
      };
      return [
        ...this.paymentAccounts,
        manualItem,
      ];
    },
    isManualPayment() {
      return this.formData.paymentAccountId === -1;
    },
    displayCSRMessageForManualPayment() {
      return (!this.formData.paymentAccountId) ? false
        : this.paymentAccountsWithManualOption.some((payment) => payment.type === 'paymentAccount.type.manual' && payment.id === this.formData.paymentAccountId);
    },
    displayCSRMessageForAutopay() {
      return (!this.formData.paymentAccountId) ? false
        : this.paymentAccountsWithManualOption.some((payment) => ['paymentAccount.type.checking', 'paymentAccount.type.credit'].includes(payment.type) && payment.id === this.formData.paymentAccountId);
    },
    createSettlePlanButtonDisabled() {
      return !this.formData.paymentAccountId
        || !this.formData.firstPaymentDate
        || (!this.formData.achConsent && !this.isManualPayment);
    },
    paymentFrequencyStr() {
      return (this.ticket.active_loan.loan_setup.payment_frequency).split('.')[2];
    },
  },

  watch: {
    'formData.paymentAccountId': {
      handler(newVal) {
        if (!newVal) {
          this.formData.achConsent = false;
        }
      },
    },
    selectedPayoffTerm(newVal) {
      if (newVal) {
        this.formData.settlementTerm = newVal.term;
        this.formData.paymentAmount = newVal.paymentAmount;
      }
    },

    selectedPayoffOption(newVal) {
      if (newVal) {
        this.formData.settlementAmount = newVal.discountedSettlementAmount;
        this.formData.optionId = newVal.option;
        this.formData.totalCreditAmount = newVal.totalCreditAmount;
        this.formData.discountPercent = newVal.discountPercent;
      }
    },
  },

  methods: {
    ...mapActions('settlement', ['createSettlementSinglePaymentPlan', 'createSettlementReducedPayoffPlan', 'getPeriodPaymentAmountForSettlementTerms']),
    ...mapActions('notification', ['setNotification']),

    goToSinglePaymentView() {
      this.$emit('display-single-payment-option-view');
    },

    async reloadPaymentProfiles() {
      const res = await this.$axios
        .post(`api/v1/loanpro-customer/${this.ticket.lpCustomer.id}/payment-profiles`, {
          ticketId: this.$route.params.id,
        });

      // eslint-disable-next-line vue/no-mutating-props
      this.paymentAccounts = [...res.data.data];
    },

    async getPaymentAmountForTermsInPlan() {
      try {
        const result = await this.getPeriodPaymentAmountForSettlementTerms({
          ticketId: this.ticket.zendesk.id,
          lpLoan: this.ticket.active_loan.id,
          data: {
            settlementAmount: this.selectedPayoffOption.discountedSettlementAmount,
            terms: this.selectedPayoffOption.terms,
          },
        });

        if (result.status === 200) {
          this.planTerms = result.data;
        }
      } catch (e) {
        this.loading = false;
        const msg = 'Something went wrong while trying to get payment amount for each terms in the plan';
        this.setNotification({
          message: msg,
          color: 'red',
        });
      }
    },

    async createSettlementReducedPayOffPlan() {
      this.loading = true;

      try {
        const result = await this.createSettlementReducedPayoffPlan({
          ticketId: this.ticket.zendesk.id,
          lpLoan: this.ticket.active_loan.id,
          formData: this.formData,
        });

        this.loading = false;

        if (result.status === 200) {
          const msg = 'Reduced Payoff Settlement Plan was completed';
          this.setNotification({
            message: msg,
            color: 'green',
          });
          this.$emit('display-settlement-final-actions');
        }
      } catch (e) {
        this.loading = false;
        const msg = 'There was an error processing your request';
        this.setNotification({
          message: msg,
          color: 'red',
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .v-radio.theme--light {
    border: 1px solid;
    margin: 0.5rem 0;
    padding: 0.5rem;
  }
  .v-radio .v-label p {
    font-weight: 600;
    font-size: 1.0rem;
  }
  .v-input--selection-controls .v-input__slot, .v-input--selection-controls .v-radio {
    background: #FFFFFF !important;
  }

  .v-input__slot {
    background: #FFFFFF !important;
  }

  .v-slot {
    background: #FFFFFF !important;
  }
</style>
